import { useMemo } from 'react';

// Numeric values matching the marker labels
// const markerAmounts = [70, 150, 300, 500, 2000, 5000, 10000, 20000];
// const markerCount = markerAmounts.length;
// const scaled = [0, 1, 2, 3, 4, 5, 6, 7]; // indices for mapping

/**
 * Hook to get the calculated amount based on sliderValue (input range value).
 * @param sliderValue number - The value of the input range (0 - 10000, etc)
 * @returns { amount: number }
 */
export function useFeeCalculator(sliderValue: number, locale: 'it' | 'es') {
  // Numeric values matching the marker labels
  // Fee brackets and marker amounts for each locale
  let markerAmounts: number[], min: number, max: number;
  if (locale === 'it') {
    markerAmounts = [70, 150, 300, 500, 2000, 5000, 10000, 20000];
    min = 0;
    max = 10000;
  } else if (locale === 'es') {
    markerAmounts = [0, 20, 150, 300, 500, 2000, 5000, 10000, 20000];
    min = 0;
    max = 10000;
  } else {
    // fallback to IT
    markerAmounts = [70, 150, 300, 500, 2000, 5000, 10000, 20000];
    min = 0;
    max = 10000;
  }
  const markerCount = markerAmounts.length;

  // Calculate the step for each marker
  const step = (max - min) / (markerCount - 1);
  // Find which two markers the sliderValue is between
  let idx = 0;
  for (let i = 0; i < markerCount - 1; i++) {
    const lower = min + i * step;
    const upper = min + (i + 1) * step;
    if (sliderValue >= lower && sliderValue <= upper) {
      idx = i;
      break;
    }
  }

  // Interpolate between markerAmounts
  const lower = min + idx * step;
  const upper = min + (idx + 1) * step;
  const t = upper === lower ? 0 : (sliderValue - lower) / (upper - lower);
  const amount = Math.round(markerAmounts[idx] + t * (markerAmounts[idx + 1] - markerAmounts[idx]));

  // Stamp fee calculation
  function calculateStampFee(amount: number): number {
    if (locale === 'it') {
      if (amount >= 70 && amount <= 150) return 1.49;
      if (amount > 150 && amount <= 300) return 3.99;
      if (amount > 300 && amount <= 500) return 9.99;
      if (amount > 500 && amount <= 2000) return 19.99;
      if (amount > 2000 && amount <= 5000) return 24.99;
      if (amount > 5000 && amount <= 10000) return 39.99;
      if (amount > 10000 && amount <= 20000) return 49.99;
      if (amount >= 20000) return Math.round(amount * 0.01 * 100) / 100;
      return 0;
    } else if (locale === 'es') {
      if (amount >= 0 && amount <= 20) return 0;
      if (amount > 20 && amount <= 150) return 1.99;
      if (amount > 150 && amount <= 300) return 3.99;
      if (amount > 300 && amount <= 500) return 9.99;
      if (amount > 500 && amount <= 2000) return 19.99;
      if (amount > 2000 && amount <= 5000) return 24.99;
      if (amount > 5000 && amount <= 10000) return 39.99;
      if (amount > 10000 && amount <= 20000) return 49.99;
      if (amount >= 20000) return Math.round(amount * 0.01 * 100) / 100;
      return 0;
    } else {
      // fallback to IT
      if (amount >= 70 && amount <= 150) return 1.49;
      if (amount > 150 && amount <= 300) return 3.99;
      if (amount > 300 && amount <= 500) return 9.99;
      if (amount > 500 && amount <= 2000) return 19.99;
      if (amount > 2000 && amount <= 5000) return 24.99;
      if (amount > 5000 && amount <= 10000) return 39.99;
      if (amount > 10000 && amount <= 20000) return 49.99;
      if (amount >= 20000) return Math.round(amount * 0.01 * 100) / 100;
      return 0;
    }
  }

  const stampFee = calculateStampFee(amount);
  const vatSaved = Math.round(amount * 0.07 * 100) / 100;

  // Calculate base and VAT using country-specific VAT rates
  const vatRate = locale === 'es' ? 0.21 : 0.22; // 21% for Spain, 22% for Italy
  const vatMultiplier = 1 + vatRate;
  const base = Math.round((amount / vatMultiplier) * 100) / 100;
  const vat = Math.round((amount - base) * 100) / 100;

  return useMemo(
    () => ({ amount, stampFee, vatSaved, base, vat }),
    [amount, stampFee, vatSaved, base, vat]
  );
}
